import { API_BASE } from './client.js'

export interface CartModifierResponse {
  id: string
  cartItemId: string
  name: string
  price: number
  quantity: number
}

export interface CartItemResponse {
  id: string
  cartId: string
  productId: string
  variantId: string | null
  name: string
  variantName: string | null
  imageUrl: string | null
  quantity: number
  unitPrice: number
  modifiersPrice: number
  totalPrice: number
  identityHash: string
  modifiers: CartModifierResponse[]
  createdAt: string
  updatedAt: string
}

export interface CartResponse {
  id: string
  organizationId: string
  locationId: string
  sessionId: string
  items: CartItemResponse[]
  total: number
  createdAt: string
  updatedAt: string
}

export interface AddItemData {
  productId: string
  variantId?: string
  quantity: number
  modifiers?: { modifierGroupId: string; modifierId: string; quantity: number }[]
}

export interface UpdateItemData {
  quantity: number
}

export class CartApiError extends Error {
  code: string
  status: number
  details?: Record<string, unknown>

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message)
    this.code = code
    this.status = status
    this.details = details
  }
}

function ensureCartSession(): string {
  let sid = localStorage.getItem('mf_cart_session')
  if (!sid) {
    sid = crypto.randomUUID()
    localStorage.setItem('mf_cart_session', sid)
  }
  return sid
}

async function cartFetch<T>(path: string, options?: RequestInit & { extraHeaders?: Record<string, string> }): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  headers['X-Cart-Session'] = ensureCartSession()
  if (options?.extraHeaders) Object.assign(headers, options.extraHeaders)
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: { code: 'CART_ERROR', message: `Cart error (${res.status})` } }))
    const code = (body?.error?.code as string) ?? 'CART_ERROR'
    const message = (body?.error?.message as string) ?? `Cart error (${res.status})`
    const details = body?.error?.details as Record<string, unknown> | undefined
    throw new CartApiError(res.status, code, message, details)
  }
  const json = await res.json()
  return json.data as T
}

export function getCart(orgSlug: string, locSlug: string): Promise<CartResponse> {
  return cartFetch<CartResponse>(`/public/${encodeURIComponent(orgSlug)}/locations/${encodeURIComponent(locSlug)}/cart`)
}

export function addCartItem(orgSlug: string, locSlug: string, data: AddItemData): Promise<CartResponse> {
  return cartFetch<CartResponse>(
    `/public/${encodeURIComponent(orgSlug)}/locations/${encodeURIComponent(locSlug)}/cart/items`,
    { method: 'POST', body: JSON.stringify(data) },
  )
}

export function updateCartItem(orgSlug: string, locSlug: string, itemId: string, data: UpdateItemData): Promise<CartResponse> {
  return cartFetch<CartResponse>(
    `/public/${encodeURIComponent(orgSlug)}/locations/${encodeURIComponent(locSlug)}/cart/items/${itemId}`,
    { method: 'PATCH', body: JSON.stringify(data) },
  )
}

export function removeCartItem(orgSlug: string, locSlug: string, itemId: string): Promise<CartResponse> {
  return cartFetch<CartResponse>(
    `/public/${encodeURIComponent(orgSlug)}/locations/${encodeURIComponent(locSlug)}/cart/items/${itemId}`,
    { method: 'DELETE' },
  )
}

export function clearCart(orgSlug: string, locSlug: string): Promise<CartResponse> {
  return cartFetch<CartResponse>(
    `/public/${encodeURIComponent(orgSlug)}/locations/${encodeURIComponent(locSlug)}/cart/items`,
    { method: 'DELETE' },
  )
}

export type CheckoutType = 'DELIVERY' | 'PICKUP' | 'DINE_IN'

export interface CheckoutRequestData {
  type: CheckoutType
  guestName: string
  guestPhone: string
  guestEmail?: string
  deliveryAddress?: string
  deliveryCoords?: { lat: number; lng: number }
  dineInTableId?: string
  comment?: string
}

export interface CheckoutResponseData {
  orderNumber: string
  publicToken: string
  status: string
  total: number
  currency: string
  type: string
  createdAt: string
}

export function checkout(
  orgSlug: string,
  locationSlug: string,
  idempotencyKey: string,
  data: CheckoutRequestData,
): Promise<CheckoutResponseData> {
  return cartFetch<CheckoutResponseData>(
    `/public/${encodeURIComponent(orgSlug)}/locations/${encodeURIComponent(locationSlug)}/checkout`,
    {
      method: 'POST',
      body: JSON.stringify(data),
      extraHeaders: { 'Idempotency-Key': idempotencyKey },
    },
  )
}